// src/admin/pages/weekend/AdminWeekendSkills.jsx
import React, { useState, useEffect } from "react";
import { weekendSkillsService, imageService } from "../../../services/api";
import { toast } from "react-toastify";

const emptyForm = () => ({ name: "", description: "", photos: "", buttonTitle: "" });

// ─── Image helpers (same format as shop: "url|alt, url|alt") ──────────────────
const parsePhotos = (photos) => {
  if (!photos || photos.trim() === "") return [];
  return photos.split(",").map((entry) => {
    const [link, alt = ""] = entry.trim().split("|");
    return { link: link || "", alt };
  });
};

const stringifyPhotos = (entries) =>
  entries
    .filter((e) => e.link.trim())
    .map((e) => `${e.link}|${e.alt || ""}`)
    .join(", ");

// ─── Small table thumbnail grid ────────────────────────────────────────────────
const PhotosPreview = ({ photos }) => {
  const images = parsePhotos(photos);
  if (!images.length) return <span style={{ color: "#aaa" }}>—</span>;
  return (
    <div style={styles.thumbGrid}>
      {images.slice(0, 4).map((img, i) => (
        <img key={i} src={img.link} alt={img.alt} style={styles.thumb} title={img.link} />
      ))}
      {images.length > 4 && (
        <div style={styles.thumbMore}>+{images.length - 4}</div>
      )}
    </div>
  );
};

// ─── Main component ────────────────────────────────────────────────────────────
const AdminWeekendSkills = ({ weekend }) => {
  const [skills, setSkills]         = useState([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState(null);
  const [formMode, setFormMode]     = useState("add");
  const [formData, setFormData]     = useState(emptyForm());
  const [currentId, setCurrentId]   = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);

  // image-manager state
  const [imageEntries, setImageEntries] = useState([]);
  const [newImageUrl, setNewImageUrl]   = useState("");
  const [newImageAlt, setNewImageAlt]   = useState("");

  useEffect(() => { if (weekend?.id) fetchSkills(); }, [weekend?.id]);

  const fetchSkills = async () => {
    try {
      setLoading(true);
      const response = await weekendSkillsService.getAll(weekend.id);
      setSkills(response.data.sort((a, b) => a.orderID - b.orderID));
    } catch {
      setError("Не вдалося завантажити навички");
    } finally {
      setLoading(false);
    }
  };

  // ── Form helpers ─────────────────────────────────────────────────────────────
  const resetForm = () => {
    setFormData(emptyForm());
    setFormMode("add");
    setCurrentId(null);
    setImageEntries([]);
    setNewImageUrl("");
    setNewImageAlt("");
  };

  const handleField = (e) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
  };

  // ── Image manager callbacks ───────────────────────────────────────────────────
  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    try {
      setUploadingImage(true);
      const links = await Promise.all(
        files.map((file: any) => imageService.upload(file, `/skills/${Date.now()}_${file.name}`))
      );
      setImageEntries((prev) => [...prev, ...links.map((link) => ({ link, alt: "" }))]);
      toast.success("Зображення успішно завантажено!");
    } catch {
      toast.error("Помилка при завантаженні зображень");
    } finally {
      setUploadingImage(false);
      e.target.value = "";
    }
  };

  const handleAddByUrl = () => {
    const trimmed = newImageUrl.trim();
    if (!trimmed) return;
    setImageEntries((prev) => [...prev, { link: trimmed, alt: newImageAlt.trim() }]);
    setNewImageUrl("");
    setNewImageAlt("");
  };

  const handleEntryChange = (index, field, value) => {
    setImageEntries((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const removeEntry = (index) => setImageEntries((prev) => prev.filter((_, i) => i !== index));

  // ── CRUD ──────────────────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    window.scrollTo(0, 0);
    const nextId = skills.length > 0 ? Math.max(...skills.map((s) => s.orderID)) + 1 : 1;
    const payload = {
      ...(formMode === "add" && { id: nextId }),
      ...formData,
      photos: stringifyPhotos(imageEntries),
    };
    try {
      if (formMode === "add") {
        await weekendSkillsService.create(weekend.id, payload);
        toast.success("Навичку успішно додано!");
      } else {
        await weekendSkillsService.update(weekend.id, currentId, payload);
        toast.success("Навичку успішно оновлено!");
      }
      resetForm();
      fetchSkills();
    } catch {
      toast.error(`Помилка при ${formMode === "add" ? "додаванні" : "оновленні"} навички`);
    }
  };

  const handleEdit = (skill) => {
    setFormData({
      name: skill.name,
      description: skill.description || "",
      photos: skill.photos || "",
      buttonTitle: skill.buttonTitle || "",
    });
    setCurrentId(skill.orderID);
    setFormMode("edit");
    setImageEntries(parsePhotos(skill.photos));
    setNewImageUrl("");
    setNewImageAlt("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Видалити цю навичку?")) return;
    try {
      await weekendSkillsService.delete(weekend.id, id);
      setSkills((prev) => prev.filter((s) => s.orderID !== id));
      toast.success("Навичку успішно видалено!");
    } catch {
      toast.error("Помилка при видаленні навички");
    }
  };

  const moveItem = async (skill, index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= skills.length) return;
    try {
      await weekendSkillsService.reorder(weekend.id, skill.orderID, newIndex);
      fetchSkills();
    } catch {
      toast.error("Помилка при зміні порядку навичок");
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────────
  if (loading) return <p style={styles.hint}>Завантаження...</p>;
  if (error)   return <p style={{ color: "#dc3545" }}>{error}</p>;

  return (
    <div>
      {/* ── Form ── */}
      <div style={styles.formContainer}>
        <h2 style={styles.formTitle}>
          {formMode === "add" ? "Додати нову навичку" : "Редагувати навичку"}
        </h2>
        <form style={styles.form} onSubmit={handleSubmit}>

          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Назва навички:</label>
              <input style={styles.input} name="name" value={formData.name}
                onChange={handleField} required />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Назва кнопки:</label>
              <input style={styles.input} name="buttonTitle"
                placeholder="Дивитися детальніше"
                value={formData.buttonTitle} onChange={handleField} required />
            </div>
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Опис:</label>
            <textarea style={styles.textArea} name="description"
              value={formData.description} onChange={handleField} required />
          </div>

          {/* ── Image Manager ── */}
          <div style={styles.formGroup}>
            <label style={styles.label}>Фото:</label>
            <div style={styles.imageEntriesContainer}>

              {/* Existing image cards */}
              {imageEntries.map((entry, index) => (
                <div key={index} style={styles.imageEntryCard}>
                  <div
                    style={styles.imagePreviewContainer}
                    onMouseEnter={(e) => {
                      const overlay: any = e.currentTarget.querySelector("[data-overlay]");
                      if (overlay) overlay.style.opacity = "1";
                    }}
                    onMouseLeave={(e) => {
                      const overlay: any = e.currentTarget.querySelector("[data-overlay]");
                      if (overlay) overlay.style.opacity = "0";
                    }}
                  >
                    {entry.link
                      ? <img src={entry.link} alt={entry.alt} style={styles.imagePreview} />
                      : <div style={styles.imagePreviewPlaceholder}><span>🖼️</span></div>
                    }
                    <div data-overlay style={styles.deleteOverlay} onClick={() => removeEntry(index)}>
                      <span style={styles.deleteOverlayText}>Видалити</span>
                    </div>
                  </div>
                  <input type="text" placeholder="URL зображення" value={entry.link}
                    onChange={(e) => handleEntryChange(index, "link", e.target.value)}
                    style={styles.imageUrlInput} />
                  <input type="text" placeholder="Alt текст" value={entry.alt}
                    onChange={(e) => handleEntryChange(index, "alt", e.target.value)}
                    style={styles.imageUrlInput} />
                </div>
              ))}

              {/* Add card */}
              <input type="file" id="skillPhotoInput" accept="image/*" multiple
                onChange={handleFileUpload} style={{ display: "none" }}
                disabled={uploadingImage} />

              <div style={styles.addPhotoCard}>
                {/* Upload section */}
                <div style={styles.addPhotoSection}
                  onClick={() => document.getElementById("skillPhotoInput")?.click()}
                  title="Завантажити з комп'ютера">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="16 16 12 12 8 16" />
                    <line x1="12" y1="12" x2="12" y2="21" />
                    <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3" />
                  </svg>
                  <span style={styles.addPhotoText}>
                    {uploadingImage ? "Завантаження..." : "Завантажити"}
                  </span>
                </div>

                <div style={styles.addPhotoDivider}>або</div>

                {/* URL section */}
                <div style={styles.addPhotoUrlSection}>
                  <input type="text" placeholder="Вставити URL..."
                    value={newImageUrl}
                    onChange={(e) => setNewImageUrl(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), handleAddByUrl())}
                    style={styles.addPhotoUrlInput} />
                  <input type="text" placeholder="Alt текст"
                    value={newImageAlt}
                    onChange={(e) => setNewImageAlt(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), handleAddByUrl())}
                    style={styles.addPhotoUrlInput} />
                  <button type="button" onClick={handleAddByUrl}
                    disabled={!newImageUrl.trim()}
                    style={{
                      ...styles.addUrlButton,
                      opacity: newImageUrl.trim() ? 1 : 0.4,
                      cursor: newImageUrl.trim() ? "pointer" : "default",
                    }}>
                    Додати
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div style={styles.buttonContainer}>
            {formMode === "edit" && (
              <button type="button" style={styles.cancelButton} onClick={resetForm}>
                Скасувати
              </button>
            )}
            <button type="submit" style={styles.button} disabled={uploadingImage}>
              {formMode === "add" ? "Додати" : "Зберегти зміни"}
            </button>
          </div>
        </form>
      </div>

      {/* ── Table ── */}
      {skills.length === 0 ? (
        <div style={styles.emptyState}><h3>Немає доданих навичок</h3></div>
      ) : (
        <div style={styles.tableContainer}>
          <table style={styles.table}>
            <thead>
              <tr style={styles.tableHeaderRow}>
                {["#", "Назва", "Опис", "Фото", "Кнопка", "Дії"].map((h) => (
                  <th key={h} style={styles.tableHeaderCell}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {skills.map((skill, index) => (
                <tr key={skill.orderID} style={styles.tableRow}>
                  <td style={styles.tableCell}>
                    <div style={styles.orderControls}>
                      <span style={styles.orderText}>{index + 1}</span>
                      <div style={styles.orderButtons}>
                        <button onClick={() => moveItem(skill, index, -1)}
                          disabled={index === 0} style={styles.iconButton}>⬆️</button>
                        <button onClick={() => moveItem(skill, index, 1)}
                          disabled={index === skills.length - 1} style={styles.iconButton}>⬇️</button>
                      </div>
                    </div>
                  </td>
                  <td style={styles.tableCell}><strong>{skill.name}</strong></td>
                  <td style={styles.tableCell}>
                    <div style={styles.descriptionCell}>{skill.description || "—"}</div>
                  </td>
                  <td style={styles.tableCell}>
                    <PhotosPreview photos={skill.photos} />
                  </td>
                  <td style={styles.tableCell}>{skill.buttonTitle || "—"}</td>
                  <td style={styles.tableCell}>
                    <div style={styles.actionButtons}>
                      <button onClick={() => handleEdit(skill)}
                        style={styles.iconButton} title="Редагувати">✏️</button>
                      <button onClick={() => handleDelete(skill.orderID)}
                        style={{ ...styles.iconButton, color: "var(--error-color)" }}
                        title="Видалити">🗑️</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ─── Styles ───────────────────────────────────────────────────────────────────
const styles: Record<string, React.CSSProperties> = {
  hint:          { padding: "2rem", textAlign: "center", color: "#666" },
  formContainer: { marginBottom: "2rem", padding: "1.5rem", backgroundColor: "#f9f9f9", borderRadius: "8px" },
  formTitle:     { marginBottom: "1rem", fontSize: "1.3rem", color: "var(--secondary-color)" },
  form:          { display: "flex", flexDirection: "column", gap: "1rem" },
  formRow:       { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" },
  formGroup:     { display: "flex", flexDirection: "column" },
  label:         { marginBottom: "0.5rem", fontWeight: 600 },
  input:         { padding: "0.8rem", border: "1px solid #ddd", borderRadius: "4px", fontFamily: "'UAFSans', sans-serif", outline: "none" },
  textArea:      { padding: "0.8rem", border: "1px solid #ddd", borderRadius: "4px", fontFamily: "'UAFSans', sans-serif", minHeight: "100px", resize: "vertical", outline: "none" },
  button:        { padding: "0.8rem 1.5rem", backgroundColor: "green", color: "white", border: "none", borderRadius: "4px", fontFamily: "'UAFSans', sans-serif", fontWeight: 500, cursor: "pointer" },
  cancelButton:  { padding: "0.8rem 1.5rem", backgroundColor: "#FFBB00", color: "white", border: "none", borderRadius: "4px", fontFamily: "'UAFSans', sans-serif", fontWeight: 500, cursor: "pointer", marginRight: "1rem" },
  buttonContainer: { display: "flex", justifyContent: "flex-end" },

  // ── Image manager ──
  imageEntriesContainer: { display: "flex", flexWrap: "wrap", gap: "1rem" },
  imageEntryCard:        { display: "flex", flexDirection: "column", gap: "0.5rem", padding: "0.75rem", backgroundColor: "white", border: "1px solid #ddd", borderRadius: "8px", width: "180px" },
  imagePreviewContainer: { position: "relative", width: "100%", height: "150px", backgroundColor: "#f5f5f5", borderRadius: "4px", overflow: "hidden" },
  imagePreview:          { width: "100%", height: "100%", objectFit: "cover" },
  imagePreviewPlaceholder: { width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "3rem", color: "#ccc" },
  deleteOverlay:         { position: "absolute", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", opacity: 0, transition: "opacity 0.2s", cursor: "pointer" },
  deleteOverlayText:     { backgroundColor: "white", color: "#333", padding: "0.5rem 1rem", borderRadius: "4px", fontSize: "0.9rem", fontWeight: 500, fontFamily: "'UAFSans', sans-serif" },
  imageUrlInput:         { padding: "0.5rem", border: "1px solid #ddd", borderRadius: "4px", fontSize: "0.85rem", fontFamily: "'UAFSans', sans-serif", outline: "none" },

  addPhotoCard:       { display: "flex", flexDirection: "column", padding: "0.75rem", width: "180px", border: "2px dashed #ccc", borderRadius: "8px", backgroundColor: "#fafafa", gap: "0.5rem", boxSizing: "border-box" },
  addPhotoSection:    { display: "flex", flexDirection: "column", alignItems: "center", gap: "0.35rem", color: "#555", cursor: "pointer", padding: "0.4rem 0", borderRadius: "6px" },
  addPhotoText:       { fontSize: "0.85rem", fontWeight: 500, fontFamily: "'UAFSans', sans-serif" },
  addPhotoDivider:    { textAlign: "center", fontSize: "0.75rem", color: "#aaa", borderTop: "1px solid #e5e5e5", paddingTop: "0.4rem", marginTop: "0.1rem" },
  addPhotoUrlSection: { display: "flex", flexDirection: "column", gap: "0.4rem" },
  addPhotoUrlInput:   { padding: "0.4rem 0.5rem", border: "1px solid #ddd", borderRadius: "4px", fontSize: "0.8rem", fontFamily: "'UAFSans', sans-serif", outline: "none", width: "100%", boxSizing: "border-box" },
  addUrlButton:       { padding: "0.4rem", backgroundColor: "green", color: "white", border: "none", borderRadius: "4px", fontFamily: "'UAFSans', sans-serif", fontSize: "0.82rem", fontWeight: 500 },

  // ── Table ──
  tableContainer:  { overflowX: "auto", marginTop: "1rem" },
  table:           { width: "100%", borderCollapse: "collapse", backgroundColor: "white", borderRadius: "8px", overflow: "hidden" },
  tableHeaderRow:  { backgroundColor: "#f8f9fa" },
  tableHeaderCell: { padding: "1rem", textAlign: "left", fontWeight: 600, borderBottom: "2px solid #dee2e6", color: "var(--secondary-color)" },
  tableRow:        { borderBottom: "1px solid #dee2e6" },
  tableCell:       { padding: "1rem", verticalAlign: "top" },
  descriptionCell: { maxWidth: "200px", wordWrap: "break-word", fontSize: "0.9rem", lineHeight: "1.4" },
  orderControls:   { display: "flex", flexDirection: "column", alignItems: "center", gap: "0.5rem" },
  orderText:       { fontSize: "1rem", fontWeight: 600 },
  orderButtons:    { display: "flex", gap: "0.25rem" },
  actionButtons:   { display: "flex", gap: "0.5rem" },
  iconButton:      { background: "none", border: "none", cursor: "pointer", fontSize: "1.2rem", padding: "0.25rem", borderRadius: "3px" },
  emptyState:      { textAlign: "center", padding: "3rem", backgroundColor: "#f9f9f9", borderRadius: "8px", marginTop: "1rem" },

  // Table photo thumbnails
  thumbGrid:  { display: "flex", flexWrap: "wrap", gap: "3px", maxWidth: "100px" },
  thumb:      { width: "42px", height: "42px", objectFit: "cover", borderRadius: "4px", border: "1px solid #ddd" },
  thumbMore:  { width: "42px", height: "42px", display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: "rgba(0,0,0,0.6)", color: "white", fontSize: "11px", fontWeight: "bold", borderRadius: "4px" },
};

export default AdminWeekendSkills;